import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";

// Enhanced fallback translations with more keys
const getFallbackTranslations = () => ({
  en: {
    translation: {
      // Homepage translations
      "homepage.howAreYouFeeling": "How are you feeling?",
      "homepage.1-3": "A few hours",
      "homepage.3-6": "Half day",
      "homepage.entireDay": "Entire day",
      "homepage.lastdays": "Last 7 Days Activity",
      "homepage.changeDate": " Another date ",
      "homepage.changeTheme": "👉 Click to change theme ",
      "homepage.pick": "Pick",
      "homepage.settings": "Settings",
      "homepage.trends": "Trends",
      "homepage.flow": "Flow",
      "homepage.relaxation": "Relaxation",
      "homepage.control": "Confident",
      "homepage.apathy": "Apathy",
      "homepage.worry": "Worry",
      "homepage.arousal": "Arousal",
      "homepage.boredom": "Boredom",
      "homepage.anxiety": "Anxiety",
      "homepage.other": "Other",
      "homepage.selectState": "What's on your mind?",
      "homepage.whatDuration": "How long did you feel",
      "homepage.whatDo": "What were you doing during this time?",
      "homepage.noMoodDataAvailable": "Time to write down your mood!",
      "homepage.team": "TeamDetails",
      "homepage.statistics": "Statistics",
      "homepage.dynamics": "Peek performance change",
      "homepage.flowPercentage": "Flow experience rate",
      "homepage.productiveHoursRate": "Peek Performance Rate: ",
      "homepage.title": "Experiences balance",
      "homepage.titleTest": "Productivity rate",
      "homepage.track": "Track",
      "homepage.delete": "Delete",
      "homepage.ai": "Explore waves of flow! Find your perfect balance.",
      "homepage.header": "Dominant mental state: Arousal",
      "homepage.peek": "Flow moments: 35%",
      "homepage.noMoodDataAvailable": "Time to write down your mood!",

      // Days
      "homepage.Mon": "Mon",
      "homepage.Tue": "Tue",
      "homepage.Wed": "Wed",
      "homepage.Thu": "Thu",
      "homepage.Fri": "Fri",
      "homepage.Sat": "Sat",
      "homepage.Sun": "Sun",

      // Common translations
      "common.loading": "Loading...",
      "common.error": "Error",
      "common.retry": "Retry",
      "common.back": "Back",

      "days.mon": "Mon",
      "days.tue": "Tue",
      "days.wed": "Wed",
      "days.thu": "Thu",
      "days.fri": "Fri",
      "days.sat": "Sat",
      "days.sun": "Sun",

      "cause.skip": "Skip",
      "cause.add": "👉 Have not found suitable cause",

      // Trends
      "trends.checkInsAmount": "Check-Ins amount:",
      "trends.topCauses": "Main feeling causes",

      // Settings
      "settings.history": "State history",
      "settings.causes": "State causes",
      "settings.version": "Version",
      "settings.aboutFlow": "About Flow Psychology",
      "settings.manageFeelingCauses": "Causes of feelings",
      "settings.delete": "Delete",
      "settings.language": "Language",
      "settings.noMoodHistoryAvailable": "No mood history available :(",
      "settings.imageTopic": "Image style",
      "settings.back": "Back",
      "settings.logout": "Log out",
      "settings.findState": "Determination of state",

      // Mood selection
      "moodSelection.save": "Save",
      "moodSelection.cancel": "Cancel",
      "moodSelection.aBitOf": "A bit of",
      "moodSelection.quiteABitOf": "Quite a bit of",
      "moodSelection.aLotOf": "A lot of",
      "moodSelection.today": "Today",
      "moodSelection.yesterday": "Yesterday",

      // Login
      "login.login": "Sign in",
      "login.signup": "Sign up",
      "login.passwordsDoNotMatch": "Passwords do not match!",
      "login.email": "E-mail",
      "login.continue": "Continue",
      "login.password": "Password",
      "login.confirmPassword": "Сonfirm password",
      "login.incorrectCredentials": "Incorrect credentials",
      "login.enterEmailAndPassword": "Please enter email and password",
      "login.emailAlreadyUsed": "E-mail is already used",

      // Mood names for getMoodNames function
      "getMoodNames.anxious": "Stress",
      "getMoodNames.worry": "Worry",
      "getMoodNames.comfort": "Confident",
      "getMoodNames.burnedOut": "Burned out",
      "getMoodNames.flow": "Flow",
      "getMoodNames.inZone": "In zone",
      "getMoodNames.deepFocus": "Deep focus",
      "getMoodNames.apathy": "Apathy",
      "getMoodNames.tired": "Tired",
      "getMoodNames.confident": "Confident",
      "getMoodNames.tension": "Tension",
      "cause.apathyQuestion": "What has caused your apathy?",
      "cause.anxietyQuestion": "What is causing your anxiety?",
      "cause.flowQuestion": "What helped you get into flow?",
      "cause.comfortQuestion": "What helped to feel the comfort?",
    },
  },
});

export async function initI18n(language) {
  const storedLang = (() => {
    try {
      return localStorage.getItem("language");
    } catch (e) {
      console.warn("❗ localStorage blocked:", e);
      return null;
    }
  })();

  const langToUse = language || storedLang || "en";

  // Check if we're online, if not, use fallback translations
  const isOnline = navigator.onLine;

  if (isOnline) {
    // Online: use HttpBackend to load translations from server
    return i18n
      .use(HttpBackend)
      .use(initReactI18next)
      .init({
        fallbackLng: "en",
        lng: langToUse,
        backend: {
          loadPath: `/locales/{{lng}}.json?v=FIXME`,
        },
        interpolation: {
          escapeValue: false,
        },
        react: {
          useSuspense: false,
        },
        // ✅ Safe fallback handler to silence WKWebView crash
        overloadTranslationOptionHandler: (...args) => args,
      });
  } else {
    // Fallback: use enhanced fallback translations
    console.log("📱 Using fallback translations");
    return i18n.use(initReactI18next).init({
      fallbackLng: "en",
      lng: langToUse,
      resources: getFallbackTranslations(),
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
      // ✅ Safe fallback handler to silence WKWebView crash
      overloadTranslationOptionHandler: (...args) => args,
    });
  }
}

// Function to reinitialize i18n when coming back online
export async function reinitializeI18nOnOnline(language) {
  const storedLang = (() => {
    try {
      return localStorage.getItem("language");
    } catch (e) {
      console.warn("❗ localStorage blocked:", e);
      return null;
    }
  })();

  const langToUse = language || storedLang || "en";

  console.log("🔄 Reinitializing i18n after coming back online");

  try {
    // First, check if we're actually online
    if (!navigator.onLine) {
      console.log("⚠️ Still offline, skipping i18n reinitialization");
      return;
    }

    // Clear existing resources to force reload
    i18n.removeResourceBundle(langToUse, "translation");

    // Reinitialize with HttpBackend to load full translations
    await i18n
      .use(HttpBackend)
      .use(initReactI18next)
      .init({
        fallbackLng: "en",
        lng: langToUse,
        backend: {
          loadPath: `/locales/{{lng}}.json?v=${Date.now()}`,
        },
        interpolation: {
          escapeValue: false,
        },
        react: {
          useSuspense: false,
        },
        // ✅ Safe fallback handler to silence WKWebView crash
        overloadTranslationOptionHandler: (...args) => args,
      });

    // Wait for translations to be fully loaded
    await i18n.loadLanguages(langToUse);

    console.log("✅ i18n reinitialized successfully with full translations");
  } catch (error) {
    console.error("❌ Failed to reinitialize i18n:", error);

    // Fallback: try to load with basic translations
    try {
      await i18n.use(initReactI18next).init({
        fallbackLng: "en",
        lng: langToUse,
        resources: getFallbackTranslations(),
        interpolation: {
          escapeValue: false,
        },
        react: {
          useSuspense: false,
        },
        overloadTranslationOptionHandler: (...args) => args,
      });
      console.log("⚠️ Fallback to basic translations loaded");
    } catch (fallbackError) {
      console.error("❌ Even fallback i18n failed:", fallbackError);
    }
  }
}

export default i18n;
